import {
  Component, Input, OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, UrlSegment } from '@angular/router';
import { first } from 'rxjs';

import { Logger } from '@sondermind/datadog-logging';
import { PillComponent } from '@sondermind/features/pill';
import { generateExperienceSupportingList } from '@sondermind/helpers/provider';
import { IrisIconModule, IconSizes } from '@sondermind/iris/icon';
import { ICertification, IProviderShareableProfile } from '@sondermind/utilities/models-public-facing';
import { USStateAbbrevMap } from '@sondermind/constants';
import { ClientTestimonialHttpService, IClientTestimonial } from '@sondermind/data-access/testimonials';
import { IAPIListResponse } from '@sondermind/utilities/models-http';

enum HeaderNames {
  ABOUT = 'About',
  SPECIALTIES = 'Specialties & Experience',
  // ORCA-902: In the PSL redesign, we are moving to 'Specializes in'
  SPECIALIZES_IN = 'Specializes In',
  INSURANCE_AND_LICENSURE = 'Insurance and Licensure',
}

const headerNameToHTMLSectionNameMap = new Map<string, string>([
  [HeaderNames.ABOUT, 'about'],
  [HeaderNames.SPECIALTIES, 'specialties'],
  [HeaderNames.SPECIALIZES_IN, 'specialties'],
  [HeaderNames.INSURANCE_AND_LICENSURE, 'insurance-and-licensure'],
]);

/**
 * This component displays tabbed, linked sections of a provider's profile data
 * To add a new section, make sure to do all of the following:
 * 1. Add the title of the section to moduleNames below
 * 2. Add the HTML of the section in the .html file
 * 3. Add an id to the <section> tag that matches the first word of the title in moduleNames + -section
 * 4. Create a link <a> tag to the section around the header, with the href being
 *   the currentRoute + a fragment of the same first word
 * 5. Add an id to that tag that matches the first word of the title in moduleNames + -link
 * This ensures the section is reachable from the header and is accessible by maintaining tab focus
 */
@Component({
  selector: 'provider-details',
  standalone: true,
  imports: [
    CommonModule,
    IrisIconModule,
    PillComponent,
  ],
  templateUrl: './provider-details.component.html',
  styleUrls: ['./provider-details.component.scss']
})
export class ProviderDetailsComponent implements OnInit {
  @Input() providerShareableProfile: IProviderShareableProfile = {} as IProviderShareableProfile;
  /**
   * False by default to show everything in a single column,
   * this allows us to programmatically (such as based on FFs) alter the layout of the page
   * */
  @Input() multiColumnDisplay: boolean = false;
  @Input() showRedesign: boolean = false; // FF value to control PSL redesign improvements

  isValid: boolean = false;
  experienceSupportingList: string[] = [];
  providerBio: string = '';
  allSortedInsurances: string[] = [];
  allOtherSortedSpecialties: string[] = [];
  topSpecialties: string[] = [];

  licensures: string[] = [];
  stateLicenses: string[] = [];

  moduleNames: string[] = [
    HeaderNames.ABOUT,
    HeaderNames.SPECIALTIES,
    HeaderNames.INSURANCE_AND_LICENSURE
  ];
  specialtiesModuleName: string = 'specialties';
  lastSelectedSection: string = this.moduleNames[0];
  fragment: string = '';
  currentRoute: string = '';

  providerBioTruncationLength: number = 500;

  iconSizes = IconSizes;

  showTestimonial: boolean = false;
  latestTestimonialFeedback: string = '';
  latestTestimonialSource: string = '';

  constructor(
    private route: ActivatedRoute,
    private logger: Logger,
    private clientTestimonialHttpService: ClientTestimonialHttpService
  ) {}

  ngOnInit(): void {
    this.providerBio = this.providerShareableProfile.bio;

    this.experienceSupportingList = generateExperienceSupportingList(
      this.providerShareableProfile.ages,
      this.providerShareableProfile.services_offered,
      this.providerShareableProfile.specialties.map(({ name }) => name)
    );

    this.route.snapshot.url.forEach((urlsegment: UrlSegment) => {
      this.currentRoute += `/${urlsegment.path.toString()}`;
    });
    this.route.fragment.subscribe((fragment) => { this.fragment = fragment });

    this.sortProviderData();
    this.handleRedesignFeatures();
    this.prepareLicenseDisplayData();
    this.prepareTestimonialDisplayData();
  }

  ngAfterViewInit(): void {
    try {
      if (this.fragment) {
        document.getElementById(`${this.fragment}-section`).scrollIntoView();
      }
    } catch (e) { this.logger.error(e) }
  }

  // Updates component based on redesign feature flag
  // Delete after redesign is complete
  private handleRedesignFeatures(): void {
    if (this.showRedesign) {
      this.moduleNames = [
        HeaderNames.ABOUT,
        HeaderNames.SPECIALIZES_IN,
        HeaderNames.INSURANCE_AND_LICENSURE
      ];

      // Remove the top specialties from the list in the redesign, since they are shown only in the pills
      if (this.topSpecialties.length > 0) {
        this.allOtherSortedSpecialties = this.allOtherSortedSpecialties
          .filter((speciality) => !this.topSpecialties.includes(speciality));
      }
    }
  }

  private sortProviderData(): void {
    this.allSortedInsurances = this.providerShareableProfile.insurances.sort(
      (ins1, ins2) => ins1.localeCompare(ins2)
    );

    this.topSpecialties = this.providerShareableProfile.top_specialties
      .filter((speciality) => speciality.priority_order !== null) // priority_order can be null
      .sort((a, b) => a.priority_order - b.priority_order)
      .map((speciality) => this.providerShareableProfile.specialties.find((s) => s.id === speciality.id).name);

    this.allOtherSortedSpecialties = this.providerShareableProfile.specialties
      .map((specialty) => specialty.name)
      .sort((s1, s2) => s1.localeCompare(s2));
  }

  // prepares license info for display
  private prepareLicenseDisplayData(): void {
    // ex. Licensed Marriage and Family Therapist (LMFT)
    const formattedLicensures: string[] = [];

    // ex. Colorado: 12345678
    const formattedStateLicenses: string[] = [];

    this.providerShareableProfile.certifications
      .filter((cert: ICertification) => !!cert.issue_state && !!cert.cert_number)
      .forEach((cert) => {
        // format licensures
        const formattedLicensure = cert.abbrev
          ? `${cert.name} (${cert.abbrev})`
          : cert.name;

        formattedLicensures.push(formattedLicensure);

        // format state licensures
        const stateName = USStateAbbrevMap[cert.issue_state] ?? cert.issue_state;
        const formattedStateLicense = `${stateName}: ${cert.cert_number}`;
        formattedStateLicenses.push(formattedStateLicense);
      });

    this.licensures = formattedLicensures;
    this.stateLicenses = formattedStateLicenses;
  }

  prepareTestimonialDisplayData(): void {
    this.clientTestimonialHttpService.getClientTestimonials(this.providerShareableProfile?.id).pipe(
      first()
    ).subscribe(
      (response: IAPIListResponse<IClientTestimonial>) => {
        const LATEST_TESTIMONIAL = response?.data.find(
          (testimonial) => !!testimonial.clientState && !!testimonial.dateFeedbackCollected && !!testimonial.feedback
        );

        if (LATEST_TESTIMONIAL) {
          this.showTestimonial = true;

          this.latestTestimonialFeedback = LATEST_TESTIMONIAL.feedback;

          const STATE_LONG_NAME = USStateAbbrevMap[LATEST_TESTIMONIAL.clientState] ?? LATEST_TESTIMONIAL.clientState;
          this.latestTestimonialSource = ` – ${STATE_LONG_NAME} • ${LATEST_TESTIMONIAL.dateFeedbackCollected}`;
        }
      }
    );
  }

  /**
   * Since we are taking in the header strings, they may have a space.
   * This function specifically trims the name down to the first word before a space, as the
   * element id will not have a space in it
   */
  scrollToSection(sectionName: string): void {
    // Section names should not have extra white space, but trim just in case
    const trimmedSectionName = sectionName.trim();
    this.lastSelectedSection = trimmedSectionName;

    const firstWordOfElementId = headerNameToHTMLSectionNameMap.get(trimmedSectionName);
    if (firstWordOfElementId) {
      const element = document.getElementById(`${firstWordOfElementId.toLocaleLowerCase()}-section`);
      const linkToFocus = document.getElementById(`${firstWordOfElementId.toLocaleLowerCase()}-link`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (linkToFocus) {
        linkToFocus.focus();
      }
    }
  }
}
